import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { activateRun, getActiveRunContext, listRuns, updateRunData } from '../Api/Run/RunApi';
import { addError, clearError } from './Error';
import { User } from './User';

export enum RunStatus {
  DATE_ONLY = 'Date Only',
  PROCESSING = 'Processing',
  PENDING_LOCK = 'Pending Lock',
  LOCKED = 'Locked'
}

export interface Run {
  id: number;
  name: string;
  status: RunStatus;
  date: string;
  rpm_run_id?: string;
  isActive?: boolean;
  statusAt?: string;
  statusBy?: User;
}

const runsSubject = new BehaviorSubject<Run[]>([] as Run[]);
const runsLoadingSubject = new BehaviorSubject<boolean>(false);
const activeRunSubject = new BehaviorSubject<Run | null>(null);

export const getRunsSubject = (): BehaviorSubject<Run[]> => runsSubject;
export const getRunsLoadingSubject = (): BehaviorSubject<boolean> => runsLoadingSubject;
export const getActiveRunSubject = (): BehaviorSubject<Run | null> => activeRunSubject;

const formatRunName = (run: Run): Run => {
  const parts = run.name.split(' ');
  if (parts.length === 2) {
    const shortYear = parts[1].slice(-2);
    return { ...run, name: `${parts[0]} ${shortYear}` };
  }
  return run;
};

export const fetchRuns = async () => {
  runsLoadingSubject.next(true);

  const { data, error } = await listRuns();
  if (data) {
    clearError('run');
    runsSubject.next(data.map(formatRunName));
  }
  if (error) addError('run', error.message);

  runsLoadingSubject.next(false);
};

export const updateRun = async (update: Partial<Run>) => {
  const { data, error } = await updateRunData(update);
  if (data) {
    clearError('run');
    fetchRuns();
  }
  if (error) addError('run', error.message);

  return { data, error };
};

export const fetchActiveRun = async () => {
  const { data, error } = await getActiveRunContext();
  if (error) {
    // Publishing null would state "no active commission run", which gates run-scoped grids across the app.
    addError('run', error.message);
    return;
  }
  clearError('run');
  activeRunSubject.next(data?.active ? formatRunName(data.active) : null);
};

export const setActiveRun = async (id: number) => {
  const { data, error } = await activateRun(id);
  if (data) {
    clearError('run');
    await Promise.all([fetchRuns(), fetchActiveRun()]);
  }
  if (error) addError('run', error.message);

  return { data, error };
};
